import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
  </svg>
);

export default function Register() {
  const { register, loginWithGoogle, formatApiError } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      toast.success(t("auth.regSuccess"));
      navigate("/app");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="absolute right-4 top-4 z-10"><LanguageSwitcher /></div>
      <div className="relative hidden lg:block">
        <img src="https://images.pexels.com/photos/8985967/pexels-photo-8985967.jpeg" alt="Mécanicien" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-blue-900/40" />
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Logo className="mb-10" />
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("auth.regTitle")}</h1>
          <p className="mt-2 text-sm text-slate-500">{t("auth.regSub")}</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">{t("auth.fullName")}</Label>
              <Input id="name" data-testid="register-name-input" required value={form.name} onChange={set("name")} placeholder={t("auth.phName")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input id="email" data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} placeholder={t("auth.phEmail")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="phone">{t("auth.phoneOpt")}</Label>
              <Input id="phone" data-testid="register-phone-input" value={form.phone} onChange={set("phone")} placeholder={t("auth.phPhone")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input id="password" data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder={t("auth.phPw6")} className="mt-1.5" />
            </div>
            {error && <p data-testid="register-error" className="text-sm text-rose-600">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full bg-blue-600 hover:bg-blue-700">
              {loading ? t("auth.signingUp") : t("auth.signUp")}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{t("common.or")}</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <Button type="button" variant="outline" onClick={loginWithGoogle} data-testid="google-register-btn"
            className="w-full gap-2.5 border-slate-300 font-medium">
            <GoogleIcon /> {t("auth.googleSignup")}
          </Button>

          <p className="mt-6 text-sm text-slate-500">
            {t("auth.haveAccount")}{" "}
            <Link to="/login" className="font-semibold text-blue-600 hover:underline" data-testid="goto-login-link">{t("auth.signIn")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
